// eslint-disable-next-line max-classes-per-file
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import isEmpty from 'lodash/isEmpty';
import RsModal from '@Pages/RetroSynthesis/components/RsModal';
import { KEYCODES } from '@Utils/KeyboardUtil';
import { ButtonWithIcon } from '@els/els-react--button';
import { KeepFocusWrapper } from '@Hooks/KeepFocus';

import './RsTooltip.scss';

class RsTooltipLayout {
  static UP = 'place-top';

  static DOWN = 'place-bottom';
}
class RsTooltipAlign {
  static LEFT = '';

  static RIGHT = 'left-align';
}
/* eslint-disable no-use-before-define */
/* eslint-disable react-hooks/exhaustive-deps */
function RsTooltip({ targetRect, children, onClosed, className, renderCloseBtn, title }) {
  const tooltipRef = useRef(null);
  const oserverRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({});
  const [tooltipLayout, setTooltipLayout] = useState(RsTooltipLayout.DOWN);
  const [tooltipAlign, setTooltipAlign] = useState(RsTooltipAlign.LEFT);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    resizeHandler();
    if (isEmpty(children)) {
      setClosed(true);
    } else {
      setClosed(false);
    }
  }, [children, closed]);

  useLayoutEffect(() => {
    if (tooltipRef && ResizeObserver) {
      oserverRef.current = new ResizeObserver(resizeHandler);
      oserverRef.current.observe(tooltipRef.current);
    }
  }, [tooltipRef]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    window.addEventListener('wheel', doClose);
    window.addEventListener('keydown', keyDownHandler);
    return () => {
      if (oserverRef.current) {
        oserverRef.current.disconnect();
      }
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('wheel', doClose);
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [children]);

  // Effect to handle outside component click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // eslint-disable-next-line no-unused-expressions
      tooltipRef.current && !tooltipRef.current.contains(event.target) && doClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tooltipRef, targetRect]);

  const resizeHandler = () => {
    if (!tooltipRef.current) {
      return;
    }
    const popupRect = tooltipRef.current.getBoundingClientRect();
    const windowScrollX = window.scrollX !== undefined ? window.scrollX : document.documentElement.scrollLeft;
    const windowScrollY = window.scrollY !== undefined ? window.scrollY : document.documentElement.scrollTop;
    const isUpDirection = targetRect.top > window.innerHeight / 2;
    const isLeftDirection = targetRect.left > window.innerWidth / 2;
    const topDown = Math.round(windowScrollY + targetRect.top + targetRect.height / 2);
    const topUp = Math.round(windowScrollY + targetRect.top - targetRect.height - popupRect.height);
    const left = Math.round(windowScrollX + targetRect.left - popupRect.width + targetRect.width / 2);
    const right = Math.round(windowScrollX + targetRect.left + targetRect.width / 2);
    setTooltipPosition({ top: `${isUpDirection ? topUp : topDown}px`, left: `${isLeftDirection ? left : right}px` });
    setTooltipLayout(isUpDirection ? RsTooltipLayout.UP : RsTooltipLayout.DOWN);
    setTooltipAlign(isLeftDirection ? RsTooltipAlign.LEFT : RsTooltipAlign.RIGHT);
  };

  const keyDownHandler = (e) => {
    if (e && e.keyCode === KEYCODES.ESC) {
      doClose();
    }
  };

  const doClose = () => {
    setClosed(true);
    onClosed();
  };

  if (!children || !targetRect) {
    return null;
  }

  return (
    <RsModal showModal={!!targetRect && !closed} showBackdrop className={cn('rs-tooltip', className)}>
      <KeepFocusWrapper needKeepFocus focusOnMount>
        <div
          className={cn('__react_component_tooltip show', tooltipLayout, tooltipAlign, 'rs-tooltip-big', 'rs-tooltip-clickable')}
          style={tooltipPosition}
          ref={tooltipRef}
          tabIndex={-1}>
          {(renderCloseBtn || title) && (
            <div className="rs-tooltip__header">
              {title && <h4 className="rs-tooltip__title u-els-padding">{title}</h4>}
              {renderCloseBtn && (
                <ButtonWithIcon
                  type="borderless"
                  size="small"
                  linkIconSize="s"
                  sprite="Close"
                  iconOnly
                  onClick={doClose}
                  data-e2e="rs-tooltip-close"
                  className="els-btn-modal-close e2e-modal-close">
                  Close tooltip
                </ButtonWithIcon>
              )}
            </div>
          )}
          <div className="tree-thumbnails__container substance__title--wrap u-els-padding-right u-els-padding-left rs-tooltip__content">{children}</div>
        </div>
      </KeepFocusWrapper>
    </RsModal>
  );
}

RsTooltip.defaultProps = {
  onClosed: () => null,
  renderCloseBtn: true,
};

RsTooltip.propTypes = {
  targetRect: PropTypes.any,
  children: PropTypes.node,
  onClosed: PropTypes.func,
  renderCloseBtn: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
};

export default RsTooltip;
